import { combineReducers } from 'redux';
import type { AnyAction } from 'redux';
import survey, * as fromSurvey from './_slices/surveys_slice';
import authentication, * as fromAuthentication from './_slices/authentication_slice';
import enrichment_center, * as fromEnrichmentCenter from './_slices/enrichment_center_slice';
import stressor_categories from './_slices/stressor_categories_slice';
import provider_titles from './_slices/provider_titles_slice';
import groups, * as fromGroups from './_slices/groups_slice';
import feedback from './_slices/feedback_slice';
import support_resources from './_slices/support_resources_slice';
import personal, * as fromPersonal from './_slices/personal_slice';
import cme_credits, * as fromCmeCredits from './_slices/cme_credits_slice';
import { createSelector } from 'reselect';
import { isBefore, subYears } from 'date-fns';
import { departments, genders, yearsInProfession } from '../_helpers/user_options';
import { showBurnoutQuestions } from '../_helpers/misc_functions';
import type { SavedWatchEntry } from '../_helpers/watch_progress';
import reported_burnout from './_slices/reported_burnout_slice';
import microlearnings from './_slices/microlearnings_slice';
import engagement from './_slices/engagement_slice';
import patient_satisfaction from './_slices/patient_satisfaction_slice';
import aiChat from './_slices/ai_chat_slice';
import achievements from './_slices/achievements_slice';
import tremendous, * as fromTremendous from './_slices/tremendous_slice';
import gamification, * as fromGamification from './_slices/gamification_slice';
import reminders, * as fromReminders from './_slices/reminders_slice';
import type { RootState } from './store';

const appReducer = combineReducers({
  authentication,
  survey,
  enrichment_center,
  feedback,
  groups,
  support_resources,
  personal,
  stressor_categories,
  cme_credits,
  provider_titles,
  reported_burnout,
  microlearnings,
  engagement,
  patient_satisfaction,
  aiChat,
  achievements,
  tremendous,
  gamification,
  reminders,
});

// BurnoutSupportMessage's record of a dismissed level-5 banner (its
// DISMISSED_AT_KEY) holds the time of that level-5 answer. It lives in
// sessionStorage, which outlasts a logout in the same tab, and older builds
// left it in localStorage; either way the next person to sign in here must not
// find it.
const DISMISSED_SUPPORT_BANNER_KEY = 'dismissedBurnoutSupportAt';

const clearDismissedSupportBanner = () => {
  for (const storage of [() => window.sessionStorage, () => window.localStorage]) {
    try {
      storage().removeItem(DISMISSED_SUPPORT_BANNER_KEY);
    } catch {
      // Storage can be unavailable (private mode, blocked site data).
    }
  }
};

type AppState = ReturnType<typeof appReducer>;

// A group picker's figures for one group are never shown under another: after
// every action, whatever the lens-dependent slices hold for a lens other than
// the current one is dropped, before any subscriber sees it, and a failed
// refetch then leaves nothing rather than the old group's figures. Without the
// demo lens build flag the lens is always null and this returns the state as is.
const keepToLens = (state: AppState): AppState => {
  const lensGroupId = fromGroups.selectLensGroupId(state);
  const kept = {
    personal: fromPersonal.keepLens(state.personal, lensGroupId),
    gamification: fromGamification.keepLens(state.gamification, lensGroupId),
    cme_credits: fromCmeCredits.keepLens(state.cme_credits, lensGroupId),
    enrichment_center: fromEnrichmentCenter.keepLens(state.enrichment_center, lensGroupId),
  };
  const unchanged = (Object.keys(kept) as (keyof typeof kept)[]).every((key) => kept[key] === state[key]);
  return unchanged ? state : { ...state, ...kept };
};

// why this "rootReducer" is declared here alongside "appReducer":
// https://alligator.io/redux/reset-state-redux/
const rootReducer = (state: AppState | undefined, action: AnyAction) => {
  if (action.type === 'authentication/logout') {
    localStorage.removeItem('currentBlock');
    localStorage.removeItem('currentSession');
    localStorage.removeItem('currentGroup');
    clearDismissedSupportBanner();
    state = undefined;
  }
  return keepToLens(appReducer(state, action));
};

export default rootReducer;

/*
-------------------------------
SELECTOR FUNCTIONS REDECLARED
why these functions are redeclared here:
https://egghead.io/lessons/javascript-redux-colocating-selectors-with-reducers
-------------------------------
*/

export const getDemographicQuestions = (state: RootState) =>
  fromSurvey.getDemographicQuestions(state.survey);

export const getChoices = (state: RootState, question: unknown) =>
  fromSurvey.getChoices(state.survey, question);

export const getBurnoutQuestion = (state: RootState) =>
  fromSurvey.getBurnoutQuestion(state.survey);

export const getMultipleChoiceQuestionsForCharts = (state: RootState) =>
  fromSurvey.getMultipleChoiceQuestionsForCharts(state.survey);

export const getAllQuestions = (state: RootState) =>
  fromSurvey.getAllQuestions(state.survey);

export const getAgreeDisagreeQuestions = (state: RootState) =>
  fromSurvey.getAgreeDisagreeQuestions(state.survey);

export const getFreeTextQuestionsForCharts = (state: RootState) =>
  fromSurvey.getFreeTextQuestionsForCharts(state.survey);

export const getCurrentUser = (state: RootState) =>
  fromAuthentication.getCurrentUser(state.authentication);

export const loggedIn = (state: RootState) =>
  fromAuthentication.loggedIn(state.authentication);

export const isAdmin = (state: RootState) =>
  fromAuthentication.isAdmin(state.authentication);

export const isAppAdmin = (state: RootState) =>
  fromAuthentication.isAppAdmin(state.authentication);

export const isOrgLeader = (state: RootState) =>
  fromAuthentication.isOrgLeader(state.authentication);

export const canSeeLocations = (state: RootState) =>
  fromAuthentication.canSeeLocations(state.authentication);

export const canSeeCompletionStatistics = (state: RootState) =>
  fromAuthentication.canSeeCompletionStatistics(state.authentication);

export const canSendReminders = (state: RootState) =>
  fromAuthentication.canSendReminders(state.authentication);

export const canSeeResults = (state: RootState) =>
  fromAuthentication.canSeeResults(state.authentication);

// PWI entitlement follows the currently selected practice group, not the user — an
// se_admin can switch into any customer, and gating must track that selection.
export const canSeePWI = (state: RootState) =>
  fromGroups.selectCurrentGroupPwiEnabled(state);

export const isDemoUser = (state: RootState) =>
  fromAuthentication.isDemoUser(state.authentication);

/*
The check-in gate's inputs: the two user-payload timestamps UserSerializer
sends (last_burnout_level_response / last_burnout_stressor_response). Each is
an ISO-8601 string, null when the user has never answered, or undefined when
the payload doesn't carry the field. src/_helpers/check_in_gate.ts turns them
into what is due, against a live clock.
*/
export const selectLastBurnoutLevelResponse = (state: RootState): unknown =>
  state.authentication.user?.last_burnout_level_response;

export const selectLastBurnoutStressorResponse = (state: RootState): unknown =>
  state.authentication.user?.last_burnout_stressor_response;

export const selectCheckInGateFields = createSelector(
  [selectLastBurnoutLevelResponse, selectLastBurnoutStressorResponse],
  (lastLevelResponse, lastStressorResponse) => ({ lastLevelResponse, lastStressorResponse })
);

export interface BurnoutAssessmentGate {
  showLevelQuestion: boolean;
  showStressorQuestion: boolean;
}

// One frozen object per outcome, so a caller subscribed through useAppSelector
// gets the same reference until the answer actually changes.
const BURNOUT_ASSESSMENT_GATES: ReadonlyArray<ReadonlyArray<Readonly<BurnoutAssessmentGate>>> =
  [false, true].map((showLevelQuestion) =>
    [false, true].map((showStressorQuestion) => Object.freeze({ showLevelQuestion, showStressorQuestion })));

/**
 * Which check-in questions are due, read against the clock each time this
 * runs. Deliberately not memoized on the two timestamps: that freezes the
 * clock at the first read, so a tab left open past the opening is never
 * prompted. useAppSelector still only re-runs it when the store changes, not
 * as time passes, so components use useCheckInGate() from
 * src/_helpers/check_in_gate.ts, which re-reads the clock when a question opens.
 */
export const showBurnoutAssessment = (state: RootState): Readonly<BurnoutAssessmentGate> => {
  const { showLevelQuestion, showStressorQuestion } = showBurnoutQuestions(
    selectLastBurnoutLevelResponse(state),
    selectLastBurnoutStressorResponse(state)
  );
  return BURNOUT_ASSESSMENT_GATES[Number(showLevelQuestion)][Number(showStressorQuestion)];
};

const selectAuthentication = (state: RootState) => state.authentication;

export const hasOutdatedDemographics = createSelector(
  [selectAuthentication],
  (authentication) => {
    const {
      burnout_location,
      provider_title,
      gender,
      years_in_profession,
      demographics_updated_at,
    } = authentication.user as Record<string, any>;

    return !(burnout_location && provider_title?.id && gender && years_in_profession) ||
      (!genders.includes(gender) ||
        !departments.includes(burnout_location) ||
        !provider_title?.enabled ||
        !yearsInProfession.includes(years_in_profession) ||
        isBefore(demographics_updated_at, subYears(new Date(), 1))
      );
  }
);

export const getCredentials = createSelector(
  [selectAuthentication],
  (authentication) => {
    const { accessToken, client, email } = authentication;
    return { accessToken, client, email };
  }
);

export const getLogoUrl = (state: RootState): string | null => {
  if (isAdmin(state)) return fromGroups.selectCurrentGroupLogoUrl(state);
  return fromAuthentication.getLogoUrl(state.authentication);
};

export const getGroupName = (state: RootState): string | null => {
  if (isAdmin(state)) return fromGroups.selectCurrentGroupName(state);
  return fromAuthentication.getGroupName(state.authentication);
};

export const getGroupId = (state: RootState) =>
  fromGroups.selectCurrentGroupId(state);

// The group a group picker's lens-dependent requests are for, or null (always,
// outside a demo-lens build); see groups_slice.
export const selectLensGroupId = (state: RootState) =>
  fromGroups.selectLensGroupId(state);

// Groups that decide which EC content is "for" the viewer: the selected group,
// otherwise the groups on their own user record. Everyone tied to a group now
// has one selected (MainPage adopts it via autoSelectOwnGroup), so the fallback
// covers users with no group at all — se_admins before they pick one. Mirrors
// User#ec_visible_group_ids.
export const getRelevantGroupIds = createSelector(
  [
    (state: RootState) => state.groups.currentGroup?.id ?? null,
    (state: RootState) => state.authentication.user?.practice_group_ids,
    (state: RootState) => state.authentication.user?.practice_group_id,
  ],
  (selectedGroupId, membershipGroupIds, primaryGroupId): Array<string | number> => {
    if (selectedGroupId !== null) return [selectedGroupId];
    return [...(membershipGroupIds ?? []), ...(primaryGroupId != null ? [primaryGroupId] : [])];
  }
);

export const getInHouseResource = (state: RootState) =>
  fromGroups.selectInHouseResource(state);

export const getSectionByRecordId = (state: RootState, record_id: string | number) =>
  fromEnrichmentCenter.getSectionByRecordId(state.enrichment_center, record_id);

export const getFilteredContent = createSelector(
  (state: RootState) => state.enrichment_center,
  (enrichment_center) => fromEnrichmentCenter.getFilteredContent(enrichment_center)
);

export const getSectionNameByRecordId = (state: RootState, record_id: string | number) =>
  fromEnrichmentCenter.getSectionNameByRecordId(state.enrichment_center, record_id);

/**
 * Memoized per record id on the library alone, so a subscriber gets the same
 * object back (and doesn't re-render) until the content itself changes.
 */
export const getContentAndDeeperWider = createSelector(
  [(state: RootState) => state.enrichment_center.content, (_state: RootState, record_id: string | number) => record_id],
  (content, record_id) => fromEnrichmentCenter.getContentAndDeeperWider({ content }, record_id)
);

/** Memoized per record id on the library, sections and categories. */
export const getSeriesForContent = createSelector(
  [
    (state: RootState) => state.enrichment_center.content,
    (state: RootState) => state.enrichment_center.sections,
    (state: RootState) => state.enrichment_center.categories,
    (_state: RootState, record_id: string | number) => record_id,
  ],
  (content, sections, categories, record_id) =>
    fromEnrichmentCenter.getSeriesForContent({ content, sections, categories }, record_id)
);

export const getSectionsWithContent = createSelector(
  (state: RootState) => state.enrichment_center.sections,
  (sections) => (sections as any[])
    .filter((s: any) => s.show && (s.ec_contents?.length ?? 0) > 0)
    .map((s: any) => s.section_name as string)
    .sort()
);

export const getSpeakersWithContent = createSelector(
  (state: RootState) => state.enrichment_center.speakers,
  (speakers) => (speakers as any[])
    .filter((s: any) => (s.ec_contents?.length ?? 0) > 0)
    .map((s: any) => s.name as string)
    .sort()
);

export const getStressorsWithContent = createSelector(
  (state: RootState) => state.enrichment_center.content,
  (state: RootState) => state.stressor_categories.categories,
  (content, categories) => {
    const stressorNames = (categories as any[]).map((s: any) => s.name as string);
    const result = new Set<string>();
    stressorNames.forEach((stressor) => {
      if ((content as any[]).some((c: any) => c.ec_content_tags?.some((t: any) => t.name === stressor)))
        result.add(stressor);
    });
    return Array.from(result).sort();
  }
);

export const getNumberOfActiveFilters = (state: RootState) =>
  fromEnrichmentCenter.getNumberOfActiveFilters(state.enrichment_center);

export const selectEnrichmentCenterContent = (state: RootState) => state.enrichment_center.content;

export const selectEnrichmentCenterSections = (state: RootState) => state.enrichment_center.sections;

export const selectEnrichmentCenterTimeWatched = (state: RootState) => state.enrichment_center.timeWatched;

/** The saved time_watched entry for one item; undefined when it was never played. */
export const selectTimeWatchedEntry = (state: RootState, record_id: string | number) =>
  state.enrichment_center.timeWatched[record_id] as SavedWatchEntry | undefined;

/** Whether the library has loaded and no time_watched fetch is in flight. A failed fetch leaves time_watched empty. */
export const selectTimeWatchedLoaded = (state: RootState) =>
  state.enrichment_center.content.length > 0 && !state.enrichment_center.fetchingTimeWatched;

/**
 * Where the Enrichment Center's library, sections and time_watched requests
 * stand. The slice has no status field, so "not fetching and no error" is the
 * closest it comes to "loaded"; a caller that needs to know a fetch was ever
 * asked for has to track that itself.
 */
export const selectEnrichmentCenterLoadState = createSelector(
  [
    (state: RootState) => state.enrichment_center.fetchingEcContent,
    (state: RootState) => state.enrichment_center.fetchingEcSections,
    (state: RootState) => state.enrichment_center.fetchingTimeWatched,
    (state: RootState) => state.enrichment_center.ecContentFetchError,
    (state: RootState) => state.enrichment_center.ecSectionsFetchError ?? null,
    (state: RootState) => state.enrichment_center.timeWatchedError,
  ],
  (fetchingContent, fetchingSections, fetchingTimeWatched, contentError, sectionsError, timeWatchedError) => ({
    fetchingContent,
    fetchingSections,
    fetchingTimeWatched,
    contentError,
    sectionsError,
    timeWatchedError,
  })
);

/**
 * Whether EcLoadingModal is up on an EC route: its showModal formula without the
 * pathname test (keep the two in step). A page that moves focus waits for this to
 * go false, because the modal takes focus while it is open and restores it on close.
 */
export const selectEcOverlayShown = (state: RootState): boolean => {
  const ec = state.enrichment_center;
  const { fetchCreditsStatus, fetchCreditsError } = state.cme_credits;
  return !!(
    ec.ecSectionsFetchError || ec.ecContentFetchError || ec.ecSpeakersFetchError ||
    ec.fetchingEcContent || ec.fetchingEcCategories || ec.fetchingEcSections || ec.fetchingEcSpeakers ||
    fetchCreditsStatus !== 'succeeded' || fetchCreditsError
  );
};

/**
 * Every library item with its time_watched entry attached (undefined when the
 * viewer has never played it). The same mapping as the slice's own
 * getContentWithTimeWatched, memoized on the two inputs so subscribers don't
 * get a new array (and re-render) on every unrelated store update.
 */
export const getContentWithTimeWatched = createSelector(
  [selectEnrichmentCenterContent, selectEnrichmentCenterTimeWatched],
  (content, timeWatched): fromEnrichmentCenter.EcContent[] =>
    content.map((item) => ({ ...item, time_watched: timeWatched[item.record_id_] }))
);

export const getSectionName = (state: RootState, section: number) =>
  fromEnrichmentCenter.getSectionName(state.enrichment_center, section);

export const getEnabledProviderTitleNames = createSelector(
  (state: RootState) => state.provider_titles.providerTitles,
  (providerTitles) => providerTitles.filter((pt) => pt.enabled).map((pt) => pt.name)
);

// Roles for the insights filter: titles actually held by users in the group
// being viewed, falling back to every enabled title when that list is empty
// (no group selected, request failed, or nothing in use).
export const getInsightRoleNames = createSelector(
  [
    (state: RootState) => state.groups.rolesInUse,
    getEnabledProviderTitleNames,
  ],
  fromGroups.insightRoleOptions
);

// Departments for the insights filter: canonical entries that at least one user
// in the group actually has. burnout_location is free text, so the canonical
// list drives the result — that keeps cost-centre strings out and preserves its
// curated order. Falls back to the full list when nothing matches.
export const getInsightDepartmentNames = createSelector(
  [(state: RootState) => state.groups.departmentsInUse],
  (departmentsInUse) => fromGroups.insightDepartmentOptions(departmentsInUse, departments as string[])
);

// Specialties for the insights filter: the group's in-use burnout_specialty
// values verbatim. No canonical list to fall back to — see insightSpecialtyOptions.
export const getInsightSpecialtyNames = createSelector(
  [(state: RootState) => state.groups.specialtiesInUse],
  fromGroups.insightSpecialtyOptions
);

// Whether the group in view offers the Specialty filter, and what it's called.
// The selected group wins where there is one; the fallback to the viewer's own
// record covers the first render, before MainPage has adopted their group.
export const getSpecialtyFilterSettings = createSelector(
  [
    (state: RootState) => state.groups.groups,
    (state: RootState) => state.groups.currentGroup,
    (state: RootState) => fromAuthentication.getOwnGroup(state.authentication),
  ],
  (groups, currentGroup, ownGroup) =>
    currentGroup
      ? fromGroups.specialtyFilterSettings(groups, currentGroup)
      : fromGroups.specialtyFilterSettingsForGroup(ownGroup)
);

// The viewer's own My Metrics data. Each returns the endpoint's
// { data, status, error } entry; every figure in it is scoped server-side to
// the signed-in user.
export const selectMyBurnoutMetrics = (state: RootState) =>
  fromPersonal.getBurnoutMetrics(state.personal);

export const selectMyCheckIn = (state: RootState) =>
  fromPersonal.getCheckIn(state.personal);

export const selectMyStressors = (state: RootState) =>
  fromPersonal.getStressors(state.personal);

export const selectMyLearning = (state: RootState) =>
  fromPersonal.getLearning(state.personal);

export const selectTremendousConnectionStatus = (state: RootState) =>
  fromTremendous.getConnectionStatus(state.tremendous);

export const selectTremendousOnboardingStarted = (state: RootState) =>
  fromTremendous.getOnboardingStarted(state.tremendous);

export const selectTremendousOnboardingStatus = (state: RootState) =>
  fromTremendous.getOnboardingStatus(state.tremendous);

export const selectTremendousOnboardingStatusLabel = (state: RootState) =>
  fromTremendous.getOnboardingStatusLabel(state.tremendous);

export const selectTremendousRewardsEnabled = (state: RootState) =>
  fromTremendous.getRewardsEnabled(state.tremendous);

export const selectTremendousPointsPerDollar = (state: RootState) =>
  fromTremendous.getPointsPerDollar(state.tremendous);

export const selectTremendousMinimumRedemptionPoints = (state: RootState) =>
  fromTremendous.getMinimumRedemptionPoints(state.tremendous);

export const selectTremendousMaximumRedemptionPoints = (state: RootState) =>
  fromTremendous.getMaximumRedemptionPoints(state.tremendous);

// Mirrors the backend's redeemable_amount_cents formula (UserPointsController#index) so it
// stays in sync as totalPoints/pointsPerDollar change, instead of being fetched/stored separately.
export const selectTremendousRedeemableAmountCents = createSelector(
  [(state: RootState) => fromGamification.getTotalPoints(state.gamification), (state: RootState) => fromTremendous.getPointsPerDollar(state.tremendous)],
  (totalPoints, pointsPerDollar) =>
    pointsPerDollar ? Math.round((totalPoints / pointsPerDollar) * 100) : null
);

export const selectUserPointsStatus = (state: RootState) =>
  fromGamification.getUserPointsStatus(state.gamification);

// The balance can be shown: it has loaded once and the latest request didn't
// fail. True during a refetch, so a revisit never blanks a figure it has.
export const selectUserPointsReady = (state: RootState) =>
  fromGamification.getUserPointsReady(state.gamification);

export const selectTremendousRedeemStatus = (state: RootState) =>
  fromTremendous.getRedeemStatus(state.tremendous);

export const selectTremendousRedeemLink = (state: RootState) =>
  fromTremendous.getRedeemLink(state.tremendous);

export const selectTremendousDisconnectStatus = (state: RootState) =>
  fromTremendous.getDisconnectStatus(state.tremendous);

export const selectTremendousSettingsUpdateStatus = (state: RootState) =>
  fromTremendous.getSettingsUpdateStatus(state.tremendous);

export const selectTremendousError = (state: RootState) =>
  fromTremendous.getTremendousError(state.tremendous);

export const selectTotalPoints = (state: RootState) =>
  fromGamification.getTotalPoints(state.gamification);

// Points earned (redemptions excluded) in the last 30 days, per GET
// /api/user_points; null until that endpoint has reported it.
export const selectPointsEarnedLast30Days = (state: RootState) =>
  fromGamification.getPointsEarnedLast30Days(state.gamification);

// How many achievements exist (GET /api/achievements), for "N of total".
export const selectAchievementsTotal = (state: RootState) =>
  state.achievements.data.length;

export const selectAchievementsTotalStatus = (state: RootState) =>
  state.achievements.status;

export const selectEarnedAchievements = (state: RootState) =>
  fromGamification.getEarnedAchievements(state.gamification);

// Award rows collapsed to badges, the way /profile/achievements shows a
// repeatable badge once. The SubNav tray and My Metrics both read these, so
// they always agree.
export const selectDistinctBadgeCount = createSelector(
  [selectEarnedAchievements],
  (rows): number => fromGamification.countDistinctBadges(rows)
);

// The badge most recently added to the collection (each achievement's first
// award, then the newest of those), or null when there is none.
export const selectNewestUnlockedBadge = createSelector(
  [selectEarnedAchievements],
  (rows) => fromGamification.newestUnlockedBadge<fromGamification.EarnedAchievementRow>(rows)
);

export const selectAchievementsStatus = (state: RootState) =>
  fromGamification.getAchievementsStatus(state.gamification);

// Earned badges can be shown (loaded once, latest request not failed).
export const selectAchievementsReady = (state: RootState) =>
  fromGamification.getAchievementsReady(state.gamification);

export const selectGamificationModalOpen = (state: RootState) =>
  fromGamification.getModalOpen(state.gamification);

export const selectGamificationModalData = (state: RootState) =>
  fromGamification.getModalData(state.gamification);

export const selectGamificationModalQueue = (state: RootState) =>
  fromGamification.getModalQueue(state.gamification);

export const selectRewardsHistory = (state: RootState) =>
  fromGamification.getRewardsHistory(state.gamification);

export const selectRewardsHistoryStatus = (state: RootState) =>
  fromGamification.getRewardsHistoryStatus(state.gamification);

export const selectRewardsHistoryError = (state: RootState) =>
  fromGamification.getRewardsHistoryError(state.gamification);

export const selectRewardLink = (state: RootState, rewardId: string) =>
  fromGamification.getRewardLink(state.gamification, rewardId);
// Leader-sent reminders. The composer's audience and history entries each carry
// the groupId they were loaded for; see reminders_slice.
export const selectReminderTemplates = (state: RootState) =>
  fromReminders.getTemplates(state.reminders);

export const selectReminderAudience = (state: RootState) =>
  fromReminders.getAudience(state.reminders);

export const selectReminderHistory = (state: RootState) =>
  fromReminders.getHistory(state.reminders);

export const selectReminderSendStatus = (state: RootState) =>
  fromReminders.getSendStatus(state.reminders);

export const selectReminderCancelStatus = (state: RootState) =>
  fromReminders.getCancelStatus(state.reminders);

export const selectLatestReminderNotice = (state: RootState) =>
  fromReminders.getLatestNotice(state.reminders);

export const selectMyReminderNoticesStatus = (state: RootState) =>
  fromReminders.getMyNoticesStatus(state.reminders);
